import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Field,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  todayInArgentina,
  useToast,
} from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Plus, Receipt } from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';
import { type Resolver, useForm, useWatch } from 'react-hook-form';

import {
  addContractPrice,
  addContractServiceTypePrice,
  listContractPrices,
  listContractServiceTypePrices,
  updateContractPrice,
  updateContractServiceTypePrice,
  type AddContractPriceInput,
  type AddContractServiceTypePriceInput,
  type ContractDetail,
  type ContractPriceBillingConcept,
  type ContractPrice,
  type ContractServiceTypePrice,
  type UpdateContractPriceInput,
  type UpdateContractServiceTypePriceInput,
} from '../../../contracts/api';
import { mapPriceError } from '../../../contracts/contract-api-errors';
import {
  DEFAULT_PRICE_FORM_VALUES,
  DEFAULT_SERVICE_TYPE_PRICE_FORM_VALUES,
  contractPriceFormSchema,
  contractServiceTypePriceFormSchema,
  type ContractPriceFormFields,
  type ContractPriceFormValues,
  type ContractServiceTypePriceFormFields,
  type ContractServiceTypePriceFormValues,
} from '../../../contracts/contract-price-form-schema';
import { formatCalendarDate } from '../../../contracts/format-calendar-date';
import { formatPriceCents } from '../../../contracts/format-price';
import {
  BILLING_CONCEPT_TYPE_LABELS,
  listBillingConceptOptions,
  type BillingConceptOption,
} from '../../../invoices/billing-concepts-api';
import {
  formatVatRate,
  listVatRateOptions,
  type VatRateOption,
} from '../../../invoices/vat-rates-api';
import {
  listServiceTypes,
  searchAllMenus,
  type MenuListItem,
  type ServiceType,
} from '../../../menus/api';
import { BulkPriceUpdateModal } from './bulk-price-update-modal';

export interface ContractPricesTabProps {
  contract: ContractDetail;
  /** `contract.price.manage`; the detail page passes the real answer. */
  canManage?: boolean;
}

/**
 * `contract-prices/spec.md` (rotating-menu delta) → "Pantalla de precios en
 * back-office": "Precio por servicio" (base, one row per service type) above
 * "Excepciones por plato" (the pre-existing per-menu table); the bulk-update
 * modal reaches both.
 */
export function ContractPricesTab({
  contract,
  canManage = true,
}: ContractPricesTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  async function invalidateAll(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contract-service-type-prices', contract.id] }),
      queryClient.invalidateQueries({ queryKey: ['contract-prices', contract.id] }),
    ]);
  }

  return (
    <div className="flex flex-col gap-8">
      <PricePrecedenceNote />

      {canManage ? (
        <div>
          <Button variant="secondary" onClick={() => setBulkModalOpen(true)}>
            Actualización masiva
          </Button>
        </div>
      ) : null}

      <ServiceTypePricesSection contract={contract} canManage={canManage} />
      <MenuPriceExceptionsSection contract={contract} canManage={canManage} />

      {bulkModalOpen ? (
        <BulkPriceUpdateModal
          contractId={contract.id}
          onClose={() => setBulkModalOpen(false)}
          onApplied={() => void invalidateAll()}
        />
      ) : null}
    </div>
  );
}

/** B3: states up front which price a customer's order line uses, in order of precedence. */
function PricePrecedenceNote(): JSX.Element {
  return (
    <div
      role="note"
      aria-label="Qué precio se usa"
      className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700"
    >
      <p className="font-medium text-slate-900">Qué precio se usa en cada línea del pedido</p>
      <ol className="mt-2 list-decimal pl-5">
        <li>
          <strong>Precio del contrato (menú)</strong>: la excepción por plato de abajo, si el plato
          tiene una vigente.
        </li>
        <li>
          <strong>Precio del contrato (servicio)</strong>: el precio por tipo de servicio, si no hay
          excepción para el plato.
        </li>
        <li>
          <strong>Precio de catálogo</strong>: el precio base del plato, solo si el contrato no tiene
          ninguno de los dos anteriores.
        </li>
      </ol>
      <p className="mt-2">
        Si el contrato marca un plato o un servicio como «no disponible», no se puede pedir: en ese
        caso no se usa el precio de catálogo.
      </p>
    </div>
  );
}

/** Same range criterion as the API: `validFrom <= today` and (`validTo` null or `>= today`). */
function isInForce(price: { validFrom: string; validTo: string | null }, today: string): boolean {
  return price.validFrom.slice(0, 10) <= today && (price.validTo === null || price.validTo.slice(0, 10) >= today);
}

interface SectionProps {
  contract: ContractDetail;
  canManage: boolean;
}

/** Active catalog rates (`/vat-rates/options`, `contract.price.manage`) feeding both price forms. */
function useVatRateOptionsQuery(): UseQueryResult<VatRateOption[]> {
  return useQuery({ queryKey: ['vat-rate-options'], queryFn: listVatRateOptions });
}

function useVatRateOptions(): VatRateOption[] {
  return useVatRateOptionsQuery().data ?? [];
}

/** Without it a failed `/vat-rates/options` would leave the selector empty and the form impossible to submit with no hint why. */
function VatRatesLoadError(): JSX.Element | null {
  const query = useVatRateOptionsQuery();

  if (!query.isError) {
    return null;
  }

  return (
    <Alert tone="danger">
      <div className="flex flex-wrap items-center gap-3">
        <span>No pudimos cargar las alícuotas de IVA.</span>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          Reintentar
        </Button>
      </div>
    </Alert>
  );
}

/** A row whose rate was deactivated after it was saved keeps its value as an extra option, so editing does not silently swap it. */
function vatSelectOptions(
  vatRates: VatRateOption[],
  currentValue: string | undefined,
): { value: string; label: string }[] {
  const options = vatRates.map((option) => ({ value: String(option.rate), label: option.name }));

  if (currentValue !== undefined && !options.some((option) => option.value === currentValue)) {
    options.push({ value: currentValue, label: formatVatRate(Number(currentValue)) });
  }

  return options;
}

/** `contract-prices/spec.md` → "Alta de precio por tipo de servicio", "Deshabilitación de tipo de servicio sin eliminar el precio". */
function ServiceTypePricesSection({ contract, canManage }: SectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);

  const pricesQuery = useQuery({
    queryKey: ['contract-service-type-prices', contract.id],
    queryFn: () => listContractServiceTypePrices(contract.id),
  });

  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });
  const vatRates = useVatRateOptions();

  const conceptsQuery = useQuery({
    queryKey: ['billing-concept-options'],
    queryFn: listBillingConceptOptions,
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['contract-service-type-prices', contract.id] });
  }

  const addMutation = useMutation({
    mutationFn: (input: AddContractServiceTypePriceInput) =>
      addContractServiceTypePrice(contract.id, input),
    onSuccess: async () => {
      await invalidate();
      setShowAddForm(false);
      toast('success', 'Precio agregado');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      priceId,
      input,
    }: {
      priceId: string;
      input: UpdateContractServiceTypePriceInput;
    }) => updateContractServiceTypePrice(contract.id, priceId, input),
    onSuccess: async () => {
      await invalidate();
      setEditingPriceId(null);
      toast('success', 'Precio actualizado');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ priceId, isAvailable }: { priceId: string; isAvailable: boolean }) =>
      updateContractServiceTypePrice(contract.id, priceId, { isAvailable }),
    onSuccess: () => invalidate(),
  });

  const serviceTypes = serviceTypesQuery.data ?? [];
  const concepts = conceptsQuery.data ?? [];
  const serviceTypeNames = Object.fromEntries(
    serviceTypes.map((serviceType) => [serviceType.id, serviceType.name]),
  );
  // A row in force blocks the fallback even when it is "no disponible" (it blocks the service).
  const pricedServiceTypeIds = new Set(
    (pricesQuery.data ?? [])
      .filter((price) => isInForce(price, todayInArgentina()))
      .map((price) => price.serviceTypeId),
  );
  const catalogFallbackServiceTypes = serviceTypes.filter(
    (serviceType) => serviceType.isActive && !pricedServiceTypeIds.has(serviceType.id),
  );

  if (pricesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (pricesQuery.isError) {
    return <Alert tone="danger">No pudimos cargar los precios por servicio.</Alert>;
  }

  const prices = pricesQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-slate-900">Precio por servicio</h2>

      {catalogFallbackServiceTypes.length > 0 ? (
        <div
          role="note"
          aria-label="Servicios con precio de catálogo"
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          Sin precio vigente en este contrato (se usa el precio de catálogo):{' '}
          {catalogFallbackServiceTypes.map((serviceType) => serviceType.name).join(', ')}.
        </div>
      ) : null}

      {showAddForm || !canManage ? null : (
        <div>
          <Button icon={Plus} onClick={() => setShowAddForm(true)}>
            Agregar precio por tipo de servicio
          </Button>
        </div>
      )}

      {addMutation.isError ? <Alert tone="danger">{mapPriceError(addMutation.error)}</Alert> : null}
      {updateMutation.isError ? (
        <Alert tone="danger">{mapPriceError(updateMutation.error)}</Alert>
      ) : null}
      {toggleMutation.isError ? (
        <Alert tone="danger">{mapPriceError(toggleMutation.error)}</Alert>
      ) : null}

      {showAddForm ? (
        <ContractServiceTypePriceForm
          serviceTypes={serviceTypes}
          concepts={concepts}
          vatRates={vatRates}
          submitLabel="Agregar precio por tipo de servicio"
          submitting={addMutation.isPending}
          onCancel={() => setShowAddForm(false)}
          onSubmit={async (values) => {
            await addMutation
              .mutateAsync({
                serviceTypeId: values.serviceTypeId,
                billingConceptId: values.billingConceptId,
                unitPriceCents: values.unitPrice,
                vatRate: values.vatRate,
                validFrom: values.validFrom,
                validTo: values.validTo === '' ? null : values.validTo,
                isAvailable: values.isAvailable,
              })
              .catch(() => undefined);
          }}
        />
      ) : null}

      {prices.length === 0 ? (
        <EmptyState icon={Receipt} title="Todavía no hay precios por tipo de servicio cargados." />
      ) : (
        <Table>
          <caption className="sr-only">Precios por tipo de servicio</caption>
          <thead>
            <tr>
              <TableHeader>Tipo de servicio</TableHeader>
              <TableHeader>Concepto</TableHeader>
              <TableHeader>Precio</TableHeader>
              <TableHeader>IVA</TableHeader>
              <TableHeader>Vigencia</TableHeader>
              <TableHeader>Disponible</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {prices.map((price) =>
              editingPriceId === price.id ? (
                <tr key={price.id} className="border-b border-slate-100">
                  <td colSpan={7} className="py-3">
                    <ContractServiceTypePriceForm
                      serviceTypes={serviceTypes}
                      concepts={concepts}
                      vatRates={vatRates}
                      currentConcept={price.billingConcept}
                      defaultValues={toServiceTypePriceFormFields(price)}
                      submitLabel="Guardar cambios"
                      submitting={updateMutation.isPending}
                      onCancel={() => setEditingPriceId(null)}
                      onSubmit={async (values) => {
                        await updateMutation
                          .mutateAsync({
                            priceId: price.id,
                            input: {
                              billingConceptId: values.billingConceptId,
                              unitPriceCents: values.unitPrice,
                              vatRate: values.vatRate,
                              validFrom: values.validFrom,
                              validTo: values.validTo === '' ? null : values.validTo,
                              isAvailable: values.isAvailable,
                            },
                          })
                          .catch(() => undefined);
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <TableRow key={price.id}>
                  <TableCell className="text-slate-900">
                    {serviceTypeNames[price.serviceTypeId] ?? price.serviceTypeId}
                  </TableCell>
                  <TableCell>
                    <span className="text-slate-900">{price.billingConcept.name}</span>{' '}
                    <Badge variant="neutral">
                      {BILLING_CONCEPT_TYPE_LABELS[price.billingConcept.arcaType]}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatPriceCents(price.unitPriceCents)}</TableCell>
                  <TableCell>{price.vatRate}%</TableCell>
                  <TableCell>{formatValidity(price)}</TableCell>
                  <TableCell>
                    <Badge variant={price.isAvailable ? 'success' : 'neutral'}>
                      {price.isAvailable ? 'Sí' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {canManage ? (
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingPriceId(price.id)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={
                          toggleMutation.isPending && toggleMutation.variables?.priceId === price.id
                        }
                        onClick={() =>
                          toggleMutation.mutate({
                            priceId: price.id,
                            isAvailable: !price.isAvailable,
                          })
                        }
                      >
                        {price.isAvailable ? 'Deshabilitar' : 'Habilitar'}
                      </Button>
                    </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/**
 * `contract-prices/spec.md` → "Resolución de precio con excepción por
 * plato": the pre-existing per-menu table, relabeled as the exception
 * mechanism it now is over the service-type price above.
 */
function MenuPriceExceptionsSection({ contract, canManage }: SectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);

  const pricesQuery = useQuery({
    queryKey: ['contract-prices', contract.id],
    queryFn: () => listContractPrices(contract.id),
  });

  const vatRates = useVatRateOptions();

  const menusQuery = useQuery({
    queryKey: ['menus-for-contract-prices'],
    queryFn: () =>
      searchAllMenus({
        q: undefined,
        serviceTypeId: undefined,
        dietaryTagId: undefined,
        excludeAllergenId: undefined,
        status: 'ACTIVE',
      }),
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['contract-prices', contract.id] });
  }

  const addMutation = useMutation({
    mutationFn: (input: AddContractPriceInput) => addContractPrice(contract.id, input),
    onSuccess: async () => {
      await invalidate();
      setShowAddForm(false);
      toast('success', 'Precio agregado');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ priceId, input }: { priceId: string; input: UpdateContractPriceInput }) =>
      updateContractPrice(contract.id, priceId, input),
    onSuccess: async () => {
      await invalidate();
      setEditingPriceId(null);
      toast('success', 'Precio actualizado');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ priceId, isAvailable }: { priceId: string; isAvailable: boolean }) =>
      updateContractPrice(contract.id, priceId, { isAvailable }),
    onSuccess: () => invalidate(),
  });

  const menus = menusQuery.data?.items ?? [];
  const menuNames = Object.fromEntries(menus.map((menu) => [menu.id, menu.name]));

  if (pricesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (pricesQuery.isError) {
    return <Alert tone="danger">No pudimos cargar la lista de precios.</Alert>;
  }

  const prices = pricesQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-slate-900">Excepciones por plato</h2>

      {showAddForm || !canManage ? null : (
        <div>
          <Button icon={Plus} onClick={() => setShowAddForm(true)}>
            Agregar precio
          </Button>
        </div>
      )}

      {addMutation.isError ? <Alert tone="danger">{mapPriceError(addMutation.error)}</Alert> : null}
      {updateMutation.isError ? (
        <Alert tone="danger">{mapPriceError(updateMutation.error)}</Alert>
      ) : null}
      {toggleMutation.isError ? (
        <Alert tone="danger">{mapPriceError(toggleMutation.error)}</Alert>
      ) : null}

      {showAddForm ? (
        <ContractPriceForm
          menus={menus}
          vatRates={vatRates}
          submitLabel="Agregar precio"
          submitting={addMutation.isPending}
          onCancel={() => setShowAddForm(false)}
          onSubmit={async (values) => {
            await addMutation
              .mutateAsync({
                menuId: values.menuId,
                unitPriceCents: values.unitPrice,
                vatRate: values.vatRate,
                validFrom: values.validFrom,
                validTo: values.validTo === '' ? null : values.validTo,
                isAvailable: values.isAvailable,
              })
              .catch(() => undefined);
          }}
        />
      ) : null}

      {prices.length === 0 ? (
        <EmptyState icon={Receipt} title="Todavía no hay precios cargados." />
      ) : (
        <Table>
          <caption className="sr-only">Excepciones por plato</caption>
          <thead>
            <tr>
              <TableHeader>Menú</TableHeader>
              <TableHeader>Precio</TableHeader>
              <TableHeader>IVA</TableHeader>
              <TableHeader>Vigencia</TableHeader>
              <TableHeader>Disponible</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {prices.map((price) =>
              editingPriceId === price.id ? (
                <tr key={price.id} className="border-b border-slate-100">
                  <td colSpan={6} className="py-3">
                    <ContractPriceForm
                      menus={menus}
                      vatRates={vatRates}
                      defaultValues={toFormFields(price)}
                      submitLabel="Guardar cambios"
                      submitting={updateMutation.isPending}
                      onCancel={() => setEditingPriceId(null)}
                      onSubmit={async (values) => {
                        await updateMutation
                          .mutateAsync({
                            priceId: price.id,
                            input: {
                              unitPriceCents: values.unitPrice,
                              vatRate: values.vatRate,
                              validFrom: values.validFrom,
                              validTo: values.validTo === '' ? null : values.validTo,
                              isAvailable: values.isAvailable,
                            },
                          })
                          .catch(() => undefined);
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <TableRow key={price.id}>
                  <TableCell className="text-slate-900">
                    {menuNames[price.menuId] ?? price.menuId}
                  </TableCell>
                  <TableCell>{formatPriceCents(price.unitPriceCents)}</TableCell>
                  <TableCell>{price.vatRate}%</TableCell>
                  <TableCell>{formatValidity(price)}</TableCell>
                  <TableCell>
                    <Badge variant={price.isAvailable ? 'success' : 'neutral'}>
                      {price.isAvailable ? 'Sí' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {canManage ? (
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingPriceId(price.id)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={
                          toggleMutation.isPending && toggleMutation.variables?.priceId === price.id
                        }
                        onClick={() =>
                          toggleMutation.mutate({
                            priceId: price.id,
                            isAvailable: !price.isAvailable,
                          })
                        }
                      >
                        {price.isAvailable ? 'Deshabilitar' : 'Habilitar'}
                      </Button>
                    </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function formatValidity(price: { validFrom: string; validTo: string | null }): string {
  const from = formatCalendarDate(price.validFrom);

  if (price.validTo === null) {
    return `Desde ${from}`;
  }

  return `${from} – ${formatCalendarDate(price.validTo)}`;
}

function toFormFields(price: ContractPrice): ContractPriceFormFields {
  return {
    menuId: price.menuId,
    unitPrice: (price.unitPriceCents / 100).toFixed(2),
    vatRate: String(price.vatRate),
    validFrom: price.validFrom.slice(0, 10),
    validTo: price.validTo === null ? '' : price.validTo.slice(0, 10),
    isAvailable: price.isAvailable,
  };
}

function toServiceTypePriceFormFields(
  price: ContractServiceTypePrice,
): ContractServiceTypePriceFormFields {
  return {
    serviceTypeId: price.serviceTypeId,
    billingConceptId: price.billingConcept.id,
    unitPrice: (price.unitPriceCents / 100).toFixed(2),
    vatRate: String(price.vatRate),
    validFrom: price.validFrom.slice(0, 10),
    validTo: price.validTo === null ? '' : price.validTo.slice(0, 10),
    isAvailable: price.isAvailable,
  };
}

/**
 * `exactOptionalPropertyTypes` treats an explicit `error={undefined}` as a
 * different (rejected) shape than the prop being absent; same helper as
 * `contract-form-page.tsx`'s own `maybeError`.
 */
function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

interface ContractPriceFormProps {
  menus: MenuListItem[];
  vatRates: VatRateOption[];
  defaultValues?: ContractPriceFormFields;
  submitLabel: string;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (values: ContractPriceFormValues) => Promise<void>;
}

/** Shared by "Agregar precio" and the inline row edit; `menuId` is fixed once a price row exists — no spec scenario allows moving a price to a different menu. */
function ContractPriceForm({
  menus,
  vatRates,
  defaultValues,
  submitLabel,
  submitting,
  onCancel,
  onSubmit,
}: ContractPriceFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ContractPriceFormFields, unknown, ContractPriceFormValues>({
    resolver: zodResolver(contractPriceFormSchema) as unknown as Resolver<
      ContractPriceFormFields,
      unknown,
      ContractPriceFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_PRICE_FORM_VALUES,
  });

  const isEditing = defaultValues !== undefined;

  // The options arrive after the form mounts: preselect the catalog default on create, once.
  const catalogDefault = vatRates.find((option) => option.isDefault);
  useEffect(() => {
    if (!isEditing && catalogDefault !== undefined && getValues('vatRate') === '') {
      setValue('vatRate', String(catalogDefault.rate));
    }
  }, [isEditing, catalogDefault, getValues, setValue]);

  const vatOptions = vatSelectOptions(vatRates, defaultValues?.vatRate);

  return (
    <form
      onSubmit={(event) => void handleSubmit((values) => onSubmit(values))(event)}
      className="flex flex-col gap-4 rounded-md border border-slate-200 p-4"
      noValidate
    >
      {isEditing ? (
        <input type="hidden" {...register('menuId')} />
      ) : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contract-price-menu" className="text-sm font-medium text-slate-900">
            Menú
            <span aria-hidden="true" className="ml-0.5 text-red-600">
              *
            </span>
          </label>
          <select
            id="contract-price-menu"
            {...register('menuId')}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="">Seleccioná un menú</option>
            {menus.map((menu) => (
              <option key={menu.id} value={menu.id}>
                {menu.name}
              </option>
            ))}
          </select>
          {errors.menuId?.message !== undefined ? (
            <p role="alert" className="text-xs text-red-600">
              {errors.menuId.message}
            </p>
          ) : null}
        </div>
      )}

      <Field
        label="Precio (en pesos, sin IVA)"
        required
        placeholder="1500.00"
        {...register('unitPrice')}
        {...maybeError(errors.unitPrice?.message)}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contract-price-vat" className="text-sm font-medium text-slate-900">
          Alícuota de IVA
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        </label>
        <VatRatesLoadError />
        <select
          id="contract-price-vat"
          {...register('vatRate')}
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Seleccioná una alícuota</option>
          {vatOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.vatRate?.message !== undefined ? (
          <p role="alert" className="text-xs text-red-600">
            {errors.vatRate.message}
          </p>
        ) : null}
      </div>

      <Field
        label="Vigente desde"
        type="date"
        required
        {...register('validFrom')}
        {...maybeError(errors.validFrom?.message)}
      />

      <Field
        label="Vigente hasta"
        type="date"
        {...register('validTo')}
        {...maybeError(errors.validTo?.message)}
      />

      <Checkbox label="Disponible" {...register('isAvailable')} />

      <div className="flex items-center gap-3">
        <Button type="submit" loading={submitting}>
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

interface ContractServiceTypePriceFormProps {
  serviceTypes: ServiceType[];
  concepts: BillingConceptOption[];
  vatRates: VatRateOption[];
  currentConcept?: ContractPriceBillingConcept;
  defaultValues?: ContractServiceTypePriceFormFields;
  submitLabel: string;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (values: ContractServiceTypePriceFormValues) => Promise<void>;
}

/** Same shape as `ContractPriceForm`, `menuId` replaced by `serviceTypeId`; `serviceTypeId` is fixed once a price row exists, same criterion as the menu exception form. */
function ContractServiceTypePriceForm({
  serviceTypes,
  concepts,
  vatRates,
  currentConcept,
  defaultValues,
  submitLabel,
  submitting,
  onCancel,
  onSubmit,
}: ContractServiceTypePriceFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors, dirtyFields },
  } = useForm<ContractServiceTypePriceFormFields, unknown, ContractServiceTypePriceFormValues>({
    resolver: zodResolver(contractServiceTypePriceFormSchema) as unknown as Resolver<
      ContractServiceTypePriceFormFields,
      unknown,
      ContractServiceTypePriceFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_SERVICE_TYPE_PRICE_FORM_VALUES,
  });

  const isEditing = defaultValues !== undefined;

  // The options arrive after the form mounts: preselect the system default on create, once.
  const systemDefaultId = concepts.find((concept) => concept.isSystemDefault)?.id;
  useEffect(() => {
    if (!isEditing && systemDefaultId !== undefined && getValues('billingConceptId') === '') {
      setValue('billingConceptId', systemDefaultId);
    }
  }, [isEditing, systemDefaultId, getValues, setValue]);

  // Prefill the rate from the chosen concept, else the catalog default; stops following once the user picks a rate themself.
  const selectedConceptId = useWatch({ control, name: 'billingConceptId' });
  const vatPrefill = (() => {
    const conceptRate = concepts.find((concept) => concept.id === selectedConceptId)?.defaultVatRate
      ?.rate;
    const assignable = vatRates.some((option) => option.rate === conceptRate);

    return assignable ? conceptRate : vatRates.find((option) => option.isDefault)?.rate;
  })();
  const vatTouched = dirtyFields.vatRate === true;
  useEffect(() => {
    if (!isEditing && !vatTouched && vatPrefill !== undefined) {
      setValue('vatRate', String(vatPrefill));
    }
  }, [isEditing, vatTouched, vatPrefill, setValue]);

  const vatOptions = vatSelectOptions(vatRates, defaultValues?.vatRate);

  // A row whose concept was deactivated is not in the active options; keep it listed so the edit does not silently swap it.
  const conceptOptions: Pick<BillingConceptOption, 'id' | 'name' | 'arcaType'>[] =
    currentConcept !== undefined && !concepts.some((concept) => concept.id === currentConcept.id)
      ? [...concepts, currentConcept]
      : concepts;

  return (
    <form
      onSubmit={(event) => void handleSubmit((values) => onSubmit(values))(event)}
      className="flex flex-col gap-4 rounded-md border border-slate-200 p-4"
      noValidate
    >
      {isEditing ? (
        <input type="hidden" {...register('serviceTypeId')} />
      ) : (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="contract-service-type-price-service-type"
            className="text-sm font-medium text-slate-900"
          >
            Tipo de servicio
            <span aria-hidden="true" className="ml-0.5 text-red-600">
              *
            </span>
          </label>
          <select
            id="contract-service-type-price-service-type"
            {...register('serviceTypeId')}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="">Seleccioná un tipo de servicio</option>
            {serviceTypes.map((serviceType) => (
              <option key={serviceType.id} value={serviceType.id}>
                {serviceType.name}
              </option>
            ))}
          </select>
          {errors.serviceTypeId?.message !== undefined ? (
            <p role="alert" className="text-xs text-red-600">
              {errors.serviceTypeId.message}
            </p>
          ) : null}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="contract-service-type-price-concept"
          className="text-sm font-medium text-slate-900"
        >
          Concepto de facturación
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        </label>
        <select
          id="contract-service-type-price-concept"
          {...register('billingConceptId')}
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Seleccioná un concepto de facturación</option>
          {conceptOptions.map((concept) => (
            <option key={concept.id} value={concept.id}>
              {concept.name} · {BILLING_CONCEPT_TYPE_LABELS[concept.arcaType]}
            </option>
          ))}
        </select>
        {errors.billingConceptId?.message !== undefined ? (
          <p role="alert" className="text-xs text-red-600">
            {errors.billingConceptId.message}
          </p>
        ) : null}
      </div>

      <Field
        label="Precio (en pesos, sin IVA)"
        required
        placeholder="1500.00"
        {...register('unitPrice')}
        {...maybeError(errors.unitPrice?.message)}
      />

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="contract-service-type-price-vat"
          className="text-sm font-medium text-slate-900"
        >
          Alícuota de IVA
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        </label>
        <VatRatesLoadError />
        <select
          id="contract-service-type-price-vat"
          {...register('vatRate')}
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Seleccioná una alícuota</option>
          {vatOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.vatRate?.message !== undefined ? (
          <p role="alert" className="text-xs text-red-600">
            {errors.vatRate.message}
          </p>
        ) : null}
      </div>

      <Field
        label="Vigente desde"
        type="date"
        required
        {...register('validFrom')}
        {...maybeError(errors.validFrom?.message)}
      />

      <Field
        label="Vigente hasta"
        type="date"
        {...register('validTo')}
        {...maybeError(errors.validTo?.message)}
      />

      <Checkbox label="Disponible" {...register('isAvailable')} />

      <div className="flex items-center gap-3">
        <Button type="submit" loading={submitting}>
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
